import { useEffect, useMemo, useState } from "react";
import { MdEmojiEvents } from "react-icons/md";
import { db } from "../lib/firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

const emptyCompetition = () => ({
  title: "",
  type: "social_media",
  status: "draft",
  rules: "",
  startAt: "",
  endAt: "",
  fields: [],
});

const socialMediaDefaultFields = () => [
  { key: "platform", label: "Platform", type: "select", required: true, options: ["Instagram", "YouTube", "Other"] },
  { key: "username", label: "Username", type: "text", required: true, options: [] },
  { key: "reelsLinks", label: "Reels Links (one per line)", type: "textarea", required: true, options: [] },
];

const toIsoOrEmpty = (v) => (v ? new Date(v).toISOString() : "");
const fromIsoToInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function Competitions() {
  const [activeTab, setActiveTab] = useState("competitions"); // competitions | submissions
  const [competitions, setCompetitions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(emptyCompetition());
  const [saving, setSaving] = useState(false);
  const [submissions, setSubmissions] = useState([]);

  useEffect(() => {
    if (!db) return;
    const q = query(collection(db, "competitions"), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setCompetitions(list);
      if (!selectedId && list.length > 0) {
        setSelectedId(list[0].id);
      }
    });
  }, [selectedId]);

  useEffect(() => {
    const c = competitions.find((x) => x.id === selectedId);
    if (!c) return;
    const startAt = c.startAt?.toDate ? c.startAt.toDate().toISOString() : c.startAt || "";
    const endAt = c.endAt?.toDate ? c.endAt.toDate().toISOString() : c.endAt || "";
    setDraft({
      title: c.title || "",
      type: c.type || "social_media",
      status: c.status || "draft",
      rules: c.rules || "",
      startAt,
      endAt,
      fields: Array.isArray(c.fields) ? c.fields : [],
    });
  }, [competitions, selectedId]);

  useEffect(() => {
    if (!db) return;
    if (activeTab !== "submissions") return;
    if (!selectedId) {
      setSubmissions([]);
      return;
    }
    const q = query(collection(db, "competitionSubmissions"), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snap) => {
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((s) => String(s.competitionId || "") === String(selectedId));
      setSubmissions(list);
    });
  }, [activeTab, selectedId]);

  const selected = useMemo(() => competitions.find((c) => c.id === selectedId) || null, [competitions, selectedId]);

  const createNew = () => {
    setSelectedId(null);
    setDraft(emptyCompetition());
  };

  const applyTypeDefaults = (type) => {
    if (type === "social_media") {
      setDraft((p) => ({ ...p, type, fields: socialMediaDefaultFields() }));
      return;
    }
    setDraft((p) => ({ ...p, type, fields: p.fields.length ? p.fields : [] }));
  };

  const addField = () => {
    setDraft((p) => ({
      ...p,
      fields: [
        ...p.fields,
        { key: `field_${p.fields.length + 1}`, label: "New Field", type: "text", required: false, options: [] },
      ],
    }));
  };

  const updateField = (idx, patch) => {
    setDraft((p) => {
      const next = [...p.fields];
      next[idx] = { ...next[idx], ...patch };
      return { ...p, fields: next };
    });
  };

  const removeField = (idx) => {
    setDraft((p) => {
      const next = [...p.fields];
      next.splice(idx, 1);
      return { ...p, fields: next };
    });
  };

  const save = async () => {
    if (!db) return;
    if (!draft.title.trim()) return;
    setSaving(true);
    try {
      const payload = {
        title: draft.title.trim(),
        type: draft.type,
        status: draft.status,
        rules: draft.rules || "",
        startAt: draft.startAt ? toIsoOrEmpty(draft.startAt) : "",
        endAt: draft.endAt ? toIsoOrEmpty(draft.endAt) : "",
        fields: Array.isArray(draft.fields) ? draft.fields : [],
        updatedAt: serverTimestamp(),
      };

      if (selectedId) {
        await updateDoc(doc(db, "competitions", selectedId), payload);
        return;
      }

      const res = await addDoc(collection(db, "competitions"), { ...payload, createdAt: serverTimestamp() });
      setSelectedId(res.id);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!db) return;
    if (!selectedId) return;
    const ok = window.confirm("Delete this competition?");
    if (!ok) return;
    await deleteDoc(doc(db, "competitions", selectedId));
    setSelectedId(null);
    setDraft(emptyCompetition());
  };

  if (!db) {
    return (
      <div className="min-h-screen p-6 bg-white text-[var(--black-text)]">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-3xl font-extrabold text-[var(--primary-solid)] flex items-center gap-3 mb-2">
            <MdEmojiEvents />
            Competitions
          </h1>
          <p className="text-sm text-gray-500">Firebase is not configured in this environment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 bg-white text-[var(--black-text)]">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-extrabold text-[var(--primary-solid)] flex items-center gap-3 mb-2">
              <MdEmojiEvents />
              Competitions
            </h1>
            <p className="text-[var(--black-text)]/70">
              Create rules, configure join forms, and review submissions
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setActiveTab("competitions")}
              className={`px-5 py-2 font-semibold rounded-xl border-2 transition ${
                activeTab === "competitions"
                  ? "bg-[var(--primary-solid)] text-white border-[var(--primary-solid)] shadow-md"
                  : "bg-white text-[var(--primary-solid)] border-gray-200 hover:bg-[var(--primary-solid)]/10"
              }`}
            >
              Competitions
            </button>
            <button
              onClick={() => setActiveTab("submissions")}
              className={`px-5 py-2 font-semibold rounded-xl border-2 transition ${
                activeTab === "submissions"
                  ? "bg-[var(--primary-solid)] text-white border-[var(--primary-solid)] shadow-md"
                  : "bg-white text-[var(--primary-solid)] border-gray-200 hover:bg-[var(--primary-solid)]/10"
              }`}
            >
              Submissions
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="font-bold text-[var(--primary-solid)]">All Competitions</div>
              <button
                onClick={createNew}
                className="px-3 py-2 rounded-lg bg-[var(--primary-solid)] text-white text-sm font-semibold hover:opacity-90"
              >
                New
              </button>
            </div>

            <div className="space-y-2">
              {competitions.length === 0 ? (
                <div className="text-sm text-gray-500 p-3">No competitions yet</div>
              ) : (
                competitions.map((c) => {
                  const active = c.id === selectedId;
                  return (
                    <button
                      key={c.id}
                      onClick={() => setSelectedId(c.id)}
                      className={`w-full text-left p-3 rounded-xl border-2 transition ${
                        active
                          ? "border-[var(--primary-solid)] bg-[var(--primary-solid)]/5"
                          : "border-gray-200 hover:border-[var(--primary-solid)]"
                      }`}
                    >
                      <div className="font-semibold truncate">{c.title || "Untitled"}</div>
                      <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                        <span className="uppercase font-bold">{String(c.status || "draft")}</span>
                        <span>•</span>
                        <span className="uppercase">{String(c.type || "custom").replace(/_/g, " ")}</span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {activeTab === "competitions" ? (
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border-2 border-gray-200 p-5">
              <div className="flex items-center justify-between gap-3 mb-5">
                <div className="font-bold text-[var(--primary-solid)]">
                  {selected ? "Edit Competition" : "Create Competition"}
                </div>
                <div className="flex items-center gap-2">
                  {selectedId && (
                    <button
                      onClick={remove}
                      className="px-4 py-2 rounded-lg border-2 border-red-200 text-red-600 font-semibold hover:bg-red-50"
                    >
                      Delete
                    </button>
                  )}
                  <button
                    onClick={save}
                    disabled={saving || !draft.title.trim()}
                    className="px-4 py-2 rounded-lg bg-[var(--primary-solid)] text-white font-semibold hover:opacity-90 disabled:opacity-50"
                  >
                    {saving ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block">Title</label>
                  <input
                    value={draft.title}
                    onChange={(e) => setDraft((p) => ({ ...p, title: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 bg-white rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
                    placeholder="Competition title"
                  />
                </div>
                <div>
                  <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block">Status</label>
                  <select
                    value={draft.status}
                    onChange={(e) => setDraft((p) => ({ ...p, status: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 bg-white rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="ended">Ended</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block">Type</label>
                  <select
                    value={draft.type}
                    onChange={(e) => applyTypeDefaults(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 bg-white rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
                  >
                    <option value="social_media">Social Media</option>
                    <option value="custom">Other / Custom</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block">Start</label>
                    <input
                      type="datetime-local"
                      value={fromIsoToInput(draft.startAt)}
                      onChange={(e) => setDraft((p) => ({ ...p, startAt: toIsoOrEmpty(e.target.value) }))}
                      className="w-full px-3 py-3 border-2 border-gray-200 bg-white rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block">End</label>
                    <input
                      type="datetime-local"
                      value={fromIsoToInput(draft.endAt)}
                      onChange={(e) => setDraft((p) => ({ ...p, endAt: toIsoOrEmpty(e.target.value) }))}
                      className="w-full px-3 py-3 border-2 border-gray-200 bg-white rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-5">
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block">Rules</label>
                <textarea
                  value={draft.rules}
                  onChange={(e) => setDraft((p) => ({ ...p, rules: e.target.value }))}
                  className="w-full min-h-[140px] px-4 py-3 border-2 border-gray-200 bg-white rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
                  placeholder="Write competition rules..."
                />
              </div>

              <div className="mt-6 flex items-center justify-between">
                <div className="font-bold text-[var(--primary-solid)]">Join Form Fields</div>
                <button
                  onClick={addField}
                  className="px-3 py-2 rounded-lg border-2 border-gray-200 text-[var(--primary-solid)] font-semibold hover:bg-[var(--primary-solid)]/10"
                >
                  Add Field
                </button>
              </div>

              <div className="mt-4 space-y-3">
                {draft.fields.length === 0 ? (
                  <div className="text-sm text-gray-500">No fields configured</div>
                ) : (
                  draft.fields.map((f, idx) => (
                    <div key={`${f.key}-${idx}`} className="border-2 border-gray-200 rounded-xl p-4">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                        <div>
                          <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block">Key</label>
                          <input
                            value={f.key || ""}
                            onChange={(e) => updateField(idx, { key: e.target.value })}
                            className="w-full px-3 py-2 border-2 border-gray-200 bg-white rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block">Label</label>
                          <input
                            value={f.label || ""}
                            onChange={(e) => updateField(idx, { label: e.target.value })}
                            className="w-full px-3 py-2 border-2 border-gray-200 bg-white rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block">Type</label>
                          <select
                            value={f.type || "text"}
                            onChange={(e) => updateField(idx, { type: e.target.value })}
                            className="w-full px-3 py-2 border-2 border-gray-200 bg-white rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
                          >
                            <option value="text">Text</option>
                            <option value="textarea">Textarea</option>
                            <option value="url">URL</option>
                            <option value="number">Number</option>
                            <option value="select">Select</option>
                          </select>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={Boolean(f.required)}
                              onChange={(e) => updateField(idx, { required: e.target.checked })}
                            />
                            Required
                          </label>
                          <button
                            onClick={() => removeField(idx)}
                            className="px-3 py-2 rounded-lg border-2 border-red-200 text-red-600 font-semibold hover:bg-red-50"
                          >
                            Remove
                          </button>
                        </div>
                      </div>

                      {f.type === "select" && (
                        <div className="mt-3">
                          <label className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 block">Options (comma separated)</label>
                          <input
                            value={Array.isArray(f.options) ? f.options.join(", ") : ""}
                            onChange={(e) =>
                              updateField(idx, {
                                options: e.target.value
                                  .split(",")
                                  .map((x) => x.trim())
                                  .filter(Boolean),
                              })
                            }
                            className="w-full px-3 py-2 border-2 border-gray-200 bg-white rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
                            placeholder="Option A, Option B, Option C"
                          />
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              <div className="mt-8 border-2 border-gray-200 rounded-xl p-4 bg-gray-50">
                <div className="font-bold text-[var(--primary-solid)] mb-3">Join Form Preview</div>
                <div className="space-y-3">
                  {draft.fields.length === 0 ? (
                    <div className="text-sm text-gray-500">No fields configured</div>
                  ) : (
                    draft.fields.map((f) => (
                      <div key={`preview-${f.key}`} className="bg-white border border-gray-200 rounded-xl p-3">
                        <div className="text-sm font-semibold">
                          {f.label || f.key} {f.required ? <span className="text-red-500">*</span> : null}
                        </div>
                        {f.type === "textarea" ? (
                          <textarea className="w-full mt-2 px-3 py-2 border-2 border-gray-200 rounded-xl text-sm" disabled />
                        ) : f.type === "select" ? (
                          <select className="w-full mt-2 px-3 py-2 border-2 border-gray-200 rounded-xl text-sm" disabled>
                            {(Array.isArray(f.options) ? f.options : []).map((opt) => (
                              <option key={opt}>{opt}</option>
                            ))}
                          </select>
                        ) : (
                          <input className="w-full mt-2 px-3 py-2 border-2 border-gray-200 rounded-xl text-sm" disabled />
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border-2 border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="font-bold text-[var(--primary-solid)]">Submissions</div>
                <div className="text-sm text-gray-500">
                  {selected ? selected.title : "Select a competition"}
                </div>
              </div>

              {selectedId ? (
                submissions.length === 0 ? (
                  <div className="text-sm text-gray-500">No submissions yet</div>
                ) : (
                  <div className="space-y-3">
                    {submissions.map((s) => (
                      <div key={s.id} className="border-2 border-gray-200 rounded-xl p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="font-semibold">
                            {String(s.userName || s.username || s.userId || "User")}
                          </div>
                          <div className="text-xs text-gray-500">
                            {s.createdAt?.toDate ? s.createdAt.toDate().toLocaleString() : ""}
                          </div>
                        </div>
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          {Object.entries(s.answers || {}).map(([k, v]) => (
                            <div key={k} className="bg-gray-50 border border-gray-200 rounded-xl p-3">
                              <div className="text-xs uppercase tracking-wider text-gray-500 font-bold">{k}</div>
                              <div className="mt-1 break-words">{String(v)}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                <div className="text-sm text-gray-500">Select a competition to see submissions</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

